/**
 * Token text: an inline run as plain text with field references as
 * `{{key}}` tokens — "Hyran betalas till {{landlord_name}} senast …".
 *
 * Lets a host edit adapted text in a plain textarea (or a chip editor that
 * serialises to the same string). `parseTokenText` turns it back into
 * inline content: `{{key}}` of a known field becomes a live reference
 * (value node with `sourceKey`), an unknown `{{x}}` stays literal text with
 * a diagnostic, `\n` is a line break. Given the run the text was made from
 * (`original`), unchanged text returns that run as is, and bold / italic
 * runs, section / attachment references and literal values whose text
 * survives verbatim (in order) are kept as nodes.
 *
 * There is no escape for a literal "{{"; text that looks like a token of an
 * unknown key simply stays text.
 */
/** `{{ key }}` — keys are slot keys (`rent_amount`, `units.0.area`) or own fields (`custom_…`). */
export const TOKEN_PATTERN = /\{\{\s*([A-Za-z0-9_.:-]{1,200})\s*\}\}/g;
export const fieldToken = (key) => `{{${key}}}`;
/** An inline run as token text: references as `{{key}}`, line breaks as `\n`, literal values as their text. */
export function toTokenText(content) {
    return content
        .map((n) => {
        switch (n.type) {
            case 'text':
                return n.text;
            case 'lineBreak':
                return '\n';
            case 'value':
                return n.sourceKey != null ? fieldToken(n.sourceKey) : (n.text ?? '');
            case 'strong':
            case 'emphasis':
            case 'label':
                return toTokenText(n.content);
            case 'image':
                return '';
            default:
                return n.text ?? n.target;
        }
    })
        .join('');
}
const textRuns = (text) => text.split('\n').flatMap((line, i) => [
    ...(i > 0 ? [{ type: 'lineBreak' }] : []),
    ...(line ? [{ type: 'text', text: line }] : []),
]);
const lookup = (fields) => {
    if (typeof fields === 'function')
        return fields;
    if (fields instanceof Map)
        return (key) => fields.get(key);
    const map = new Map();
    for (const f of fields)
        map.set(f.key, f);
    return (key) => map.get(key);
};
const keysIn = (content, out = []) => {
    for (const n of content) {
        if (n.type === 'value' && n.sourceKey != null)
            out.push(n.sourceKey);
        else if (n.type === 'strong' || n.type === 'emphasis' || n.type === 'label')
            keysIn(n.content, out);
    }
    return out;
};
/** Nodes kept whole across an edit when their token text survives verbatim. */
const isAtom = (n) => n.type === 'value' || n.type === 'sectionRef' || n.type === 'attachmentRef' || n.type === 'strong' || n.type === 'emphasis';
/** Parses a gap of free text: tokens of known fields become references. */
function parseGap(text, offset, field, preview, diagnostics) {
    const out = [];
    let cursor = 0;
    let literal = '';
    for (const m of text.matchAll(TOKEN_PATTERN)) {
        const at = m.index ?? 0;
        const key = m[1];
        const f = field(key);
        literal += text.slice(cursor, at);
        cursor = at + m[0].length;
        if (!f) {
            literal += m[0];
            diagnostics.push({ severity: 'warning', code: 'unknown_field', key, index: offset + at, message: `Unknown field "${key}" — kept as text.` });
            continue;
        }
        out.push(...textRuns(literal));
        literal = '';
        const value = { type: 'value', text: preview?.(key) ?? null, sourceKey: key, ...(f.format ? { format: f.format } : {}) };
        out.push(value);
    }
    literal += text.slice(cursor);
    out.push(...textRuns(literal));
    return out;
}
/**
 * Token text → inline content. See the module comment for the rules.
 * Unknown `{{x}}` → literal text + an `unknown_field` diagnostic.
 */
export function parseTokenText(text, options) {
    const normalized = text.replace(/\r\n?/g, '\n');
    const original = options.original ?? [];
    const field = lookup(options.fields);
    const diagnostics = [];
    if (options.original && normalized === toTokenText(original)) {
        const content = [...original];
        return { content, changed: false, references: keysIn(content), detached: [], diagnostics };
    }
    const content = [];
    const origTotal = toTokenText(original).length;
    let cursor = 0;
    let origOffset = 0;
    let prevOrigEnd = 0;
    for (const node of original) {
        const len = toTokenText([node]).length;
        if (!isAtom(node)) {
            origOffset += len;
            continue;
        }
        const needle = toTokenText([node]);
        // Where the atom would sit if only text after it (or only before it) changed.
        const fromStart = cursor + (origOffset - prevOrigEnd);
        const fromEnd = normalized.length - (origTotal - origOffset);
        const distance = (at) => Math.min(Math.abs(at - fromStart), Math.abs(at - fromEnd));
        let best = -1;
        if (needle) {
            for (let at = normalized.indexOf(needle, cursor); at !== -1; at = normalized.indexOf(needle, at + 1)) {
                if (best === -1 || distance(at) < distance(best))
                    best = at;
            }
        }
        if (best !== -1) {
            content.push(...parseGap(normalized.slice(cursor, best), cursor, field, options.preview, diagnostics), node);
            cursor = best + needle.length;
            prevOrigEnd = origOffset + len;
        }
        origOffset += len;
    }
    content.push(...parseGap(normalized.slice(cursor), cursor, field, options.preview, diagnostics));
    const references = keysIn(content);
    const remaining = new Map();
    for (const k of references)
        remaining.set(k, (remaining.get(k) ?? 0) + 1);
    const detached = [];
    for (const k of keysIn(original)) {
        const n = remaining.get(k) ?? 0;
        if (n > 0)
            remaining.set(k, n - 1);
        else if (!detached.includes(k))
            detached.push(k);
    }
    return { content, changed: true, references, detached, diagnostics };
}
//# sourceMappingURL=tokens.js.map